// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { ChevronDown } from 'lucide-react';
import { EducBox, ChartBox, ResultCard, InputRow, EDUC, baseChartOpts, fmt } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

const CostiNascosti = ({ importo, valoreImmobile, istruttoria, isCasaPrima }) => {
  const P = parseFloat(importo) || 200000;
  const valImm = parseFloat(valoreImmobile) || P * 1.25;
  const istr = parseFloat(istruttoria) || 1000;
  const valCatastale = P * 0.65;
  const impostasostitutiva = isCasaPrima ? P * 0.0025 : P * 0.02;
  const impostaRegistro = isCasaPrima ? valCatastale * 0.02 : valCatastale * 0.09;
  // Spese notarili 1-2% del valore immobile (mid: 1.5%)
  const notaio = valImm * 0.015;
  const perizia = 350;
  const totale = impostasostitutiva + impostaRegistro + notaio + perizia + istr;
  return (
    <div className="mt-4 p-4 bg-[#252523] rounded-xl border border-white/[0.06]">
      <p className="text-sm font-medium text-[#E8E8E4] mb-3">Costi nascosti ({isCasaPrima ? 'Prima casa' : 'Altra destinazione'})</p>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-[#888780]">Imposta sostitutiva ({isCasaPrima ? '0.25%' : '2%'} del capitale erogato)</span><span className="text-[#E8E8E4]">€{fmt(impostasostitutiva)}</span></div>
        <div className="flex justify-between"><span className="text-[#888780]">Imposta registro ({isCasaPrima ? '2%' : '9%'} sul val. catastale)</span><span className="text-[#E8E8E4]">€{fmt(impostaRegistro)}</span></div>
        <div className="flex justify-between"><span className="text-[#888780]">Spese notarili (~1.5% del valore immobile)</span><span className="text-[#E8E8E4]">€{fmt(notaio)}</span></div>
        <div className="flex justify-between"><span className="text-[#888780]">Perizia</span><span className="text-[#E8E8E4]">€{fmt(perizia)}</span></div>
        <div className="flex justify-between"><span className="text-[#888780]">Istruttoria</span><span className="text-[#E8E8E4]">€{fmt(istr)}</span></div>
        <div className="flex justify-between font-semibold border-t border-white/[0.06] pt-2 mt-2">
          <span className="text-[#888780]">Totale costi nascosti</span><span className="text-[#C9A84C]">€{fmt(totale)}</span>
        </div>
      </div>
    </div>
  );
};

const MutuoCalc = () => {
  const url = useDeepLinkParams({ importo: 'num', tasso: 'num', durata: 'num' });
  const [importo, setImporto] = useState(url.importo ?? '200000');
  const [tasso, setTasso] = useState(url.tasso ?? '3.5');
  const [durata, setDurata] = useState(url.durata ?? '25');
  const [casaPrima, setCasaPrima] = useState(true);
  const [valoreImmobile, setValoreImmobile] = useState('250000');
  const [istruttoria, setIstruttoria] = useState('1000');
  // Deployment 17 — P1: spese perizia in sezione collassata
  const [perizia, setPerizia] = useState('300');
  const [showCostiAggiuntivi, setShowCostiAggiuntivi] = useState(false);
  const [showCosti, setShowCosti] = useState(false);

  useEffect(() => {
    if (hasAllParams(url, ['importo', 'durata', 'tasso'])) scrollToResult('mutuo-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const P = parseFloat(importo) || 0;
  const r = parseFloat(tasso) / 100 / 12;
  const n = parseInt(durata) * 12;
  const rata = r > 0 ? P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : P / n;
  const totaleInteressi = rata * n - P;
  const costTotale = rata * n;
  const valImm = parseFloat(valoreImmobile) || 0;
  const ltv = valImm > 0 ? (P / valImm) * 100 : 0;
  // Detrazione interessi 19% su max €4.000/anno (solo prima casa)
  const interessiAnnoMedio = totaleInteressi / Math.max(1, parseInt(durata));
  const detrazioneInteressi = casaPrima ? Math.min(interessiAnnoMedio, 4000) * 0.19 : 0;

  // Deployment 17 — P1: TAEG mutuo (IRR bisezione su capitale_netto = capitale - istruttoria - perizia)
  const istrV = parseFloat(istruttoria) || 0;
  const periziaV = parseFloat(perizia) || 0;
  const taegMutuo = (() => {
    if (P <= 0 || rata <= 0 || n <= 0) return 0;
    const capitaleNetto = P - istrV - periziaV;
    if (capitaleNetto <= 0) return 0;
    const f = (rm) => {
      let pv = 0;
      for (let t = 1; t <= n; t++) pv += rata / Math.pow(1 + rm, t);
      return capitaleNetto - pv;
    };
    let a = 1e-6, b = 0.5, fa = f(a);
    for (let i = 0; i < 80; i++) {
      const m = (a + b) / 2;
      const fm = f(m);
      if (Math.abs(fm) < 1e-10 || (b - a) / 2 < 1e-10) break;
      if (fm * fa > 0) { a = m; fa = fm; } else { b = m; }
    }
    const mid = (a + b) / 2;
    return (Math.pow(1 + mid, 12) - 1) * 100;
  })();

  const labelsYearly = Array.from({ length: parseInt(durata) + 1 }, (_, i) => i % 2 === 0 ? `Anno ${i}` : '');
  const dataCapitale = labelsYearly.map((_, i) => {
    const nPaid = i * 12;
    const remaining = r > 0 ? P * Math.pow(1 + r, nPaid) - rata * (Math.pow(1 + r, nPaid) - 1) / r : Math.max(0, P - (P / n) * nPaid);
    return Math.max(0, Math.round(remaining));
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <EducBox text={EDUC.mutuo} />
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <InputRow label="Importo mutuo (€)" value={importo} onChange={setImporto} prefix="€" step={100} min={0} max={10000000} />
        <InputRow label="Valore immobile (€)" value={valoreImmobile} onChange={setValoreImmobile} prefix="€" step={100} min={0} max={10000000} tooltip="Prezzo dell'immobile da acquistare — usato per calcolare LTV e imposte" />
        <InputRow label="Tasso annuo (%)" value={tasso} onChange={setTasso} suffix="%" step={0.1} min={0} max={30} />
        <InputRow label="Durata (anni)" value={durata} onChange={setDurata} suffix="anni" step={1} min={1} max={50} />
        <InputRow label="Spese istruttoria (€)" value={istruttoria} onChange={setIstruttoria} prefix="€" step={100} min={0} max={10000000} tooltip="Varia per banca — verifica il TAEG del tuo istituto" />
      </div>
      <div className="flex gap-3 mb-4" data-testid="mutuo-destinazione">
        <button onClick={() => setCasaPrima(true)} data-testid="mutuo-prima-casa-btn"
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${casaPrima ? 'bg-[#3D7A52] text-white' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>Prima casa</button>
        <button onClick={() => setCasaPrima(false)} data-testid="mutuo-altra-btn"
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${!casaPrima ? 'bg-[#3D7A52] text-white' : 'bg-[#252523] text-[#888780] border border-white/[0.08]'}`}>Altra destinazione</button>
      </div>
      {valImm > 0 && (
        <div className="mb-4 p-3 rounded-lg text-xs" data-testid="mutuo-ltv-note"
          style={{ background: ltv > 80 ? 'rgba(232,144,58,0.08)' : 'rgba(61,122,82,0.06)',
                   border: `1px solid ${ltv > 80 ? 'rgba(232,144,58,0.3)' : 'rgba(61,122,82,0.25)'}` }}>
          <p className="text-[#E8E8E4]"><strong>LTV (Loan-to-Value): {ltv.toFixed(1)}%</strong></p>
          <p className="text-[#888780] mt-1">LTV sotto 80%: condizioni standard. LTV tra 80% e 100%: spread più alto e polizza assicurativa obbligatoria — alcune banche non concedono mutui oltre l'80% del valore.</p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-4 mb-4" data-testid="mutuo-result">
        <ResultCard label="Rata mensile" value={`€${fmt(rata)}`} green />
        <ResultCard label="Costo totale" value={`€${fmt(costTotale)}`} />
        <ResultCard label="Interessi totali" value={`€${fmt(totaleInteressi)}`} gold />
      </div>

      {/* Deployment 17 — P1: TAEG mutuo */}
      <div className="mb-4 p-4 rounded-xl grid sm:grid-cols-2 gap-4" data-testid="mutuo-taeg-box"
        style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div>
          <p className="text-xs text-[#888780] mb-1">TAN (solo interessi)</p>
          <p className="text-2xl font-bold text-[#E8E8E4]" data-testid="mutuo-tan-value">{(parseFloat(tasso) || 0).toFixed(2)}%</p>
          <p className="text-xs text-[#888780] mt-1">Tasso nominale dichiarato dalla banca</p>
        </div>
        <div>
          <p className="text-xs text-[#888780] mb-1">TAEG (costo reale totale)</p>
          <p className="text-2xl font-bold text-[#C9A84C]" data-testid="mutuo-taeg-value">{taegMutuo.toFixed(2)}%</p>
          <p className="text-xs text-[#C9A84C] mt-1">
            Include TAN, spese istruttoria e perizia. Richiedi sempre il PIES (Prospetto Informativo Europeo Standardizzato) alla tua banca prima di firmare.
          </p>
          <p className="text-[10px] text-[#888780] mt-2 italic">
            L'imposta sostitutiva (0.25% prima casa / 2% altra destinazione) non è inclusa nel TAEG — è un'imposta fiscale separata.
          </p>
        </div>
      </div>

      {/* Costi aggiuntivi expandable (Deployment 17 P1) */}
      <button
        onClick={() => setShowCostiAggiuntivi(!showCostiAggiuntivi)}
        className="text-sm text-[#C9A84C] hover:text-[#E8E8E4] transition-colors mb-3"
        data-testid="mutuo-costi-toggle"
      >
        {showCostiAggiuntivi ? '▲ Nascondi' : '▼ Mostra'} costi aggiuntivi (perizia)
      </button>
      {showCostiAggiuntivi && (
        <div className="mb-4 grid sm:grid-cols-2 gap-4" data-testid="mutuo-costi-aggiuntivi">
          <InputRow
            label="Spese perizia (€)"
            value={perizia}
            onChange={setPerizia}
            prefix="€"
            step={50}
            min={0}
            max={5000}
            tooltip="Costo della perizia tecnica dell'immobile da parte della banca — di solito €200-500"
            data-testid="mutuo-perizia"
          />
        </div>
      )}
      {casaPrima && (
        <div className="mb-4 p-3 rounded-lg text-xs" data-testid="mutuo-detrazione-note"
          style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <p className="text-[#C9A84C] font-semibold">Detrazione fiscale (Prima casa)</p>
          <p className="text-[#888780] mt-1">19% sugli interessi passivi fino a max €4.000/anno → risparmio fiscale stimato <strong className="text-[#E8E8E4]">€{fmt(detrazioneInteressi)}/anno</strong>.</p>
        </div>
      )}
      <ShareCalcButton slug="mutuo" testid="mutuo-share-btn"
        getValues={() => ({ importo, tasso, durata })} />
      <button onClick={() => setShowCosti(!showCosti)} className="text-sm text-[#C9A84C] hover:text-[#E8E8E4] transition-colors mb-2 mt-3">
        {showCosti ? '▲ Nascondi' : '▼ Mostra'} costi nascosti acquisto
      </button>
      {showCosti && <CostiNascosti importo={importo} valoreImmobile={valoreImmobile} istruttoria={istruttoria} isCasaPrima={casaPrima} />}
      <ChartBox title="Debito residuo nel tempo" height={420}>
        <Line
          data={{
            labels: labelsYearly,
            datasets: [{ label: 'Debito residuo', data: dataCapitale, borderColor: '#C9A84C', backgroundColor: 'rgba(201,168,76,0.1)', fill: true, tension: 0.4, pointRadius: 0 }],
          }}
          options={baseChartOpts()}
        />
      </ChartBox>

      {/* Accordion approfondimenti */}
      <MutuoAccordion />
    </motion.div>
  );
};

const MUTUO_ITEMS = [
  { title: '🏛️ CONSAP — Fondo di Garanzia Prima Casa', body: 'Il Fondo CONSAP permette allo Stato di garantire fino all\'80% del mutuo per chi non ha sufficienti garanzie. Ideale per under 36, giovani coppie e lavoratori atipici. Non è un sussidio — è una garanzia statale che ti permette di ottenere il mutuo anche con meno risparmi.\nRequisiti principali: prima casa non di lusso, ISEE ≤ €40.000 per le categorie prioritarie.\n→ Usa il Calcolatore CONSAP (/calcolatori/consap)' },
  { title: '📊 Ammortamento alla francese vs italiano', body: 'La maggior parte dei mutui italiani usa l\'ammortamento alla francese: rate costanti per tutta la durata. All\'inizio paghi quasi solo interessi, alla fine quasi solo capitale.\nEsempio: mutuo €200.000 a 25 anni al 3.5% → prima rata €145 capitale + €583 interessi. Ultima rata: €725 capitale + €3 interessi.\nL\'ammortamento italiano ha rate decrescenti — quota capitale fissa, interessi calanti. Costa meno in totale ma le prime rate sono più alte. Quello alla francese è più prevedibile nel budget mensile.' },
  { title: '🛑 Soglia di sussistenza', body: 'Le banche verificano che dopo la rata ti rimanga abbastanza per vivere. Soglia tipica: €600-700/mese per persona singola, +€300-400 per ogni componente aggiuntivo.\nCoppia senza figli → soglia ~€1.200/mese. Se il tuo stipendio netto è €2.000 e la rata è €900, rimangono €1.100 — sotto soglia, il mutuo potrebbe essere rifiutato.\nRegola pratica: la rata non deve superare il 30-35% del reddito netto familiare.' },
  { title: '🏗️ Mutuo SAL (Stato Avanzamento Lavori)', body: 'Il mutuo SAL viene erogato a tranche in base all\'avanzamento dei lavori. Non ricevi tutto subito — la banca eroga quote man mano che il geometra certifica lo stato di avanzamento.\nVantaggio: paghi interessi solo sulla quota erogata, non sull\'intero importo.\nIdeale per: costruzione su terreno di proprietà, ristrutturazione pesante, acquisto in costruzione da costruttore.' },
  { title: '🌱 Mutuo green', body: 'Tassi agevolati (spread -0.1-0.3%) per immobili con classe energetica A o B. Con la direttiva europea Case Green 2030 che richiede la riqualificazione degli immobili in classe E o inferiore, comprare o ristrutturare verso classi alte è sempre più conveniente. Chiedi sempre alla banca se hanno un\'offerta mutuo green — non sempre la propongono spontaneamente.' },
];

const MutuoAccordion = () => {
  const [open, setOpen] = useState(null);
  return (
    <div className="mt-6" style={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden' }}>
      <div className="px-4 py-3" style={{ background: '#252523', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-sm font-medium text-[#E8E8E4]">Approfondimenti sul mutuo</p>
      </div>
      {MUTUO_ITEMS.map((item, i) => (
        <div key={i} style={{ borderBottom: i < MUTUO_ITEMS.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02] transition-colors">
            <span className="text-sm text-[#E8E8E4]">{item.title}</span>
            <ChevronDown className={`w-4 h-4 text-[#C9A84C] shrink-0 ml-3 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {open === i && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
                <div className="px-4 pb-4 text-sm text-[#888780] leading-relaxed whitespace-pre-line">{item.body}</div>
</motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export default MutuoCalc;
